import {
  Alert,
  type AlertErrorHandlerFactory,
  type AlertRetryWrapperFactory,
} from './Alert'
import { action, observable } from 'mobx'
import { firstValueFrom, race } from 'rxjs'
import { extractMessageFromError } from '@/utils/error-util'
import { isAbortError } from '@/utils/abort-util'
import { fromAbortSignal } from '@/utils/rx-util'

/**
 * Manages App Notifications (the ones rendered inside the app, not related to Desktop Notifications).
 */
export class AlertsManager {
  /**
   * The visible alerts.
   */
  @observable accessor alerts: Alert[] = []

  /**
   * Creates a new Alert.
   */
  @action.bound
  newAlert(): Alert {
    const alert: Alert = new Alert(
      () => this._addAlert(alert),
      () => this._removeAlert(alert),
    )

    return alert
  }

  /**
   * Adds the alert to the list.
   *
   * @param alert
   */
  @action.bound
  private _addAlert(alert: Alert) {
    this.alerts.unshift(alert)
  }

  /**
   * Removes the alert from the list.
   *
   * @param alert
   */
  @action.bound
  private _removeAlert(alert: Alert) {
    const idx = this.alerts.indexOf(alert)
    if (idx === -1) {
      return
    }

    this.alerts.splice(idx, 1)
  }
}

/**
 * Creates an alert error handler factory.
 *
 * @param manager
 */
export function createAlertErrorHandlerFactory(
  manager: AlertsManager,
): AlertErrorHandlerFactory {
  return (title) =>
    action('alertErrorHandler', function alertErrorHandler(err: unknown) {
      if (!isAbortError(err)) {
        manager
          .newAlert()
          .withState('error')
          .saying(title, extractMessageFromError(err))
          .autoDismiss(20_000)
          .show()
      }
      throw err
    })
}

/**
 * Creates an alert retry wrapper factory.
 * The wrapper function will not complete until either the call is successful,
 * or the alert is dismissed without retrying, in which case the
 * last error is thrown.
 *
 * @param manager
 */
export function createAlertRetryWrapperFactory(
  manager: AlertsManager,
): AlertRetryWrapperFactory {
  return (title, signal) => async (fn) => {
    while (true) {
      signal?.throwIfAborted()
      try {
        return await fn()
      } catch (err: unknown) {
        if (signal?.aborted || isAbortError(err)) {
          throw err
        }

        const retry = await shouldRetry(err)
        if (!retry) {
          throw err
        }
      }
    }

    /**
     * Shows an alert for the error and waits for it to be retried or dismissed.
     *
     * @param err The error.
     * @return `true` if the call should be retried;
     *  `false` if the error should be thrown and the retry loop aborted.
     */
    async function shouldRetry(err: unknown): Promise<boolean> {
      let retry = false
      const alert = manager
        .newAlert()
        .withState('error')
        .saying(title, extractMessageFromError(err))
        .withActions({
          title: 'Try again',
          handler() {
            retry = true
          },
        })
        .show()

      // The alert emits `dismissed$` before it runs the action handler, so
      // `retry` is only read after the await, once the handler has run.
      // An abort re-enters the loop, which then throws on the signal.
      await firstValueFrom(race(alert.dismissed$, fromAbortSignal(signal)))
      alert.dismiss()
      return retry || !!signal?.aborted
    }
  }
}
